import React, { useEffect, useState, ChangeEvent, useRef } from "react";
import { useHistory } from 'react-router-dom'
import { connect } from "react-redux";
import { LocationState, Dispatcher, RootState, ILocationListAllModel, IBlockModel, ILiftModel, ILocationConstructionModel } from "AppTypes";
import { locationActions } from "./state_management/actions";
import { appActions } from "@app/state_management/actions";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Modal } from "../../../components/Modal";
import { Modal as ModalBootstrap, Spinner } from 'react-bootstrap'
import { success, error } from '@services/toast-service'
import { useDisplayMode } from '@utils/useDisplayMode'
import { api } from '@services'
import { scanPngLayer, parseLayerFromFilename, buildCreateBatch2DPayload } from '@utils/pngMeshScan'
import "./LocationSetting.css";
 
type Props = {
    dispatch: Dispatcher;
    locationState: LocationState;
};


const LocationSetting: React.FC<Props> = ({ locationState, dispatch }) => {

    const { dwgUploadUpdateFile, glbUploadUpdateFile, pngUploadUpdateFiles, dwgUploadFile, glbUploadFile, pngUploadFiles, blockList, liftList, constructionModelList, selectedConstructionModelId, locationList, deletingLocationId, updatingLocation, insertingLocation, isUnderMeshLoading } = locationState;


    const { is2D } = useDisplayMode() // #8426: 2D = upload PNG, no dwg/glb
    const history = useHistory()
    const uploadDwgFileButtonRef = useRef<HTMLInputElement>(null);
    const uploadGlbFileButtonRef = useRef<HTMLInputElement>(null);
    const uploadPngFilesButtonRef = useRef<HTMLInputElement>(null);
    const uploadDwgFileUpdateButtonRef = useRef<HTMLInputElement>(null);
    const uploadGlbFileUpdateButtonRef = useRef<HTMLInputElement>(null);
    const uploadPngFilesUpdateButtonRef = useRef<HTMLInputElement>(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
    const mounted = useRef(false);
    // SKY-166: what the [isUnderMeshLoading] effect below must do once the spinner turns
    // off. Every path of handleAddClick turns the spinner off — the errors included — and
    // the effect used to treat that as "saved", showing 「保存が完了しました。」 next to the
    // error message.
    //   'none'             nothing was saved: keep the form, no message
    //   'saved'            reload the lists, reset the form, 「保存が完了しました。」
    //   'savedWithoutMesh' the location row was created but its 2D mesh was not: reload
    //                      the lists so the row shows; the handler already showed the error
    const saveOutcomeRef = useRef<'none' | 'saved' | 'savedWithoutMesh'>('none');

    useEffect(() => {
        dispatch(locationActions.fetchBlockList());
        dispatch(locationActions.fetchLiftList());
        dispatch(locationActions.fetchConstructionModel());
    }, []);

    //モデル保存後再更新
    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }

        if (isUnderMeshLoading) return;

        const outcome = saveOutcomeRef.current;
        saveOutcomeRef.current = 'none';
        if (outcome === 'none') return;

        dispatch(locationActions.fetchBlockList());
        dispatch(locationActions.fetchLiftList());
        dispatch(locationActions.fetchConstructionModel());
        dispatch(locationActions.fetchLocationList(selectedConstructionModelId));
        dispatch(locationActions.resetLocationInsert());
        // SKY-168: resetLocationInsert does not touch the chosen PNGs, and the browser's file
        // input keeps showing them until its value is cleared — clear both, otherwise the next
        // 登録 would silently reuse the previous files.
        dispatch(locationActions.setPngUploadFiles(null));
        if (uploadPngFilesButtonRef.current) {
            uploadPngFilesButtonRef.current.value = '';
        }

        if (outcome === 'saved') {
            success("保存が完了しました。");
        }
    }, [isUnderMeshLoading]);


    useEffect(() => {
        if (selectedConstructionModelId) {
            dispatch(locationActions.fetchLocationList(selectedConstructionModelId));
        }
    }, [selectedConstructionModelId]);
  
    useEffect(() => {
        if (
            constructionModelList?.length > 0 &&
            !constructionModelList.find(cm => cm.id === selectedConstructionModelId)
        ) {
            dispatch(locationActions.setSelectedConstructionModelId(constructionModelList[0].id));
        }
    }, [constructionModelList]);
    
    const handleDwgFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget?.value && e.currentTarget.files?.[0]) {
            dispatch(locationActions.setDwgUploadFile(e.currentTarget.files?.[0]));
        }
    }

    const handleGlbFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget?.value && e.currentTarget.files?.[0]) {
            dispatch(locationActions.setGlbUploadFile(e.currentTarget.files?.[0]));
        }
    }

    const handlePngFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
        // SKY-168: the picker is the browser's own file input now, so the state must follow
        // exactly what it shows — including an empty selection (dialog cancelled).
        dispatch(locationActions.setPngUploadFiles(e.currentTarget.files));
    }

    const handleGlbRowFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget?.value && e.currentTarget.files?.[0]) {
            dispatch(locationActions.setGlbUploadUpdateFile(e.currentTarget.files?.[0]));
        }
    }
    const handlePngRowFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget?.value && e.currentTarget.files?.length) {
            dispatch(locationActions.setPngUploadUpdateFiles(e.currentTarget.files));
        }
    }
    const handleDwgRowFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget?.value && e.currentTarget.files?.[0]) {
            dispatch(locationActions.setDwgUploadUpdateFile(e.currentTarget.files?.[0]));
        }
    }

    const handleClickUploadDwgButton = () => {
        if (uploadDwgFileButtonRef && uploadDwgFileButtonRef.current) {
            uploadDwgFileButtonRef.current.click();
        }

    }
    const handleClickUploadGlbButton = () => {
        if (uploadGlbFileButtonRef && uploadGlbFileButtonRef.current) {
            uploadGlbFileButtonRef.current.click();
        }

    }
    const handleClickUploadDwgUpdateButton = () => {
        if (uploadDwgFileUpdateButtonRef && uploadDwgFileUpdateButtonRef.current) {
            uploadDwgFileUpdateButtonRef.current.click();
        }

    }
    const handleClickUploadGlbUpdateButton = () => {
        if (uploadGlbFileUpdateButtonRef && uploadGlbFileUpdateButtonRef.current) {
            uploadGlbFileUpdateButtonRef.current.click();
        }

    }


    const handleAddClick = async () => {

        if (!selectedConstructionModelId) {
            error("全体モデルを選択してください。");
            return;
        }
        if (!insertingLocation.blockId) {
            error("ブロックを選択してください。");
            return;
        }
        if (!insertingLocation.liftId) {
            error("リフトを選択してください。");
            return;
        }

        // #8426: only 3D mode requires dwg/glb; 2D needs PNG only
        if (!is2D) {
            if (!dwgUploadFile || !dwgUploadFile.name || dwgUploadFile?.size <= 0) {
                error("dwgファイルを選択してください。");
                return;
            }
            if (!dwgUploadFile.name.endsWith(".dwg")) {
                error("dwgファイル以外は登録できません。");
                return;
            }
            if (!glbUploadFile || !glbUploadFile.name || glbUploadFile?.size <= 0) {
                error("glbファイルを選択してください。");
                return;
            }
            if (!glbUploadFile.name.endsWith(".glb")) {
                error("glbファイル以外は登録できません。");
                return;
            }
        }
        if (!pngUploadFiles || pngUploadFiles.length <= 0) {
            error("pngファイルを選択してください。");
            return;
        }
        for (var i = 0; i < pngUploadFiles?.length; i++) {
            if (!pngUploadFiles[i].name.endsWith(".png")) {
                error("pngファイル以外は選択できません。");
                return;
            }
        }

        //if (!selectedConstructionModelId || !insertingLocation || !insertingLocation.blockId || !insertingLocation.liftId) {
        //    error("無効なデータ");
        //    return;
        //}

        const formData = new FormData();

        // #8426: only send dwg/glb in 3D mode (already validated non-null above when !is2D)
        if (!is2D) {
            formData.append(
                "dwgFile",
                dwgUploadFile!,
                dwgUploadFile!.name
            );

            formData.append(
                "glbFile",
                glbUploadFile!,
                glbUploadFile!.name
            );
        }

        //png
        if (pngUploadFiles) {
            for (var i = 0; i < pngUploadFiles?.length; i++) {
                formData.append(
                    "pngFiles",
                    pngUploadFiles[i],
                    pngUploadFiles[i].name
                );
            }
        }

        formData.append(
            "blockId",
            insertingLocation.blockId

        );

        formData.append(
            "liftId",
            insertingLocation.liftId

        );

        formData.append(
            "constructionModelId",
            selectedConstructionModelId

        );
        try {
            saveOutcomeRef.current = 'none';
            dispatch(locationActions.setUnderMeshLoading(true));
            const response = await dispatch(locationActions.insertLocation(formData));

            // まず保存結果チェック
            if (!(response.payload && response.payload.success)) {
                error(response.payload.errors[0]);
                dispatch(locationActions.setUnderMeshLoading(false));
                return;
            }
            saveOutcomeRef.current = 'saved';
            //この後に、「UnderMeshProcessJob」が完了するまでロード状態を表示する。
            //完了後、リロードする。

            // #8426 (2D): scan the uploaded PNGs and register the mesh grid via Mesh/CreateBatch2D.
            // FE scans (R=marker, G=pumpNumber, B=row, layer=filename); BE only stores the grid.
            // NEEDS-VALIDATION: scan thresholds in utils/pngMeshScan.ts must be checked against a real customer PNG.
            if (is2D && pngUploadFiles && pngUploadFiles.length > 0) {
                try {
                    const listRes: any = await api.locationAPI.fetchLocationByConstructionModel(selectedConstructionModelId);
                    const createdList: any[] = listRes?.data ?? [];
                    const created = createdList.find(
                        (x) => `${x.blockId}`.toLowerCase() === `${insertingLocation.blockId}`.toLowerCase()
                            && `${x.liftId}`.toLowerCase() === `${insertingLocation.liftId}`.toLowerCase()
                    );
                    const newLocationId = created?.id;
                    if (newLocationId) {
                        const files = Array.from(pngUploadFiles);
                        // SKY-161: scanPngLayer also returns the per-pixel row map used to
                        // derive the under-relations from overlapping cell shapes.
                        const scannedLayers = await Promise.all(
                            files.map((f) => scanPngLayer(f, parseLayerFromFilename(f.name)))
                        );
                        const payload = buildCreateBatch2DPayload(newLocationId, scannedLayers, true);
                        await api.meshAPI.createBatch2D(payload);
                    }
                } catch (scanErr) {
                    // The location row was saved; only the 2D mesh-grid registration failed.
                    // SKY-166: report it instead of 「保存が完了しました。」 — e.g. PNGs of
                    // different sizes, or two PNGs for the same layer (both rejected, SKY-161).
                    // eslint-disable-next-line no-console
                    console.error('2D mesh grid registration (CreateBatch2D) failed:', scanErr);
                    saveOutcomeRef.current = 'savedWithoutMesh';
                    error("打重ね画像の登録に失敗しました。pngファイルを確認してください。");
                }
            }

            // #8426 (2D): 2D has no UnderMeshProcessJob / socket completion event (that's 3D-only,
            // Main.tsx TYPE_MESSAGE_SOCKET[5]), so the loading overlay would spin forever. The 2D
            // work above is synchronous, so turn the spinner off here — this also fires the
            // [isUnderMeshLoading] effect that reloads the list and shows 「保存が完了しました。」.
            if (is2D) {
                dispatch(locationActions.setUnderMeshLoading(false));
            }

        } catch {
            // SKY-166: the catch variable used to be named `error`, shadowing the toast
            // function, so this branch threw a TypeError instead of showing a message.
            error('システムに問題が発生しました。');
            dispatch(locationActions.setUnderMeshLoading(false));
        }
    }

    const handleUpdateButtonClick = async (id: string) => {
        await dispatch(locationActions.setUpdatingLocationId(id));
    }

    const handleUpdateCancelButtonClick = async (id: string) => {
        await dispatch(locationActions.resetLocationUpdate());

    }
    const handleDeleteCancelButtonClick = async () => {
        setShowDeleteConfirmModal(false);
        await dispatch(locationActions.setDeletingLiftId(''));

    }

    const handleUpdateButtonSaveClick = async (id: string) => {

        if (!selectedConstructionModelId) {
            error("全体モデルを選択してください。");
            return;
        }
        if (!updatingLocation.blockId) {
            error("ブロックを選択してください。");
            return;
        }
        if (!updatingLocation.liftId) {
            error("リフトを選択してください。");
            return;
        }

        //if (!dwgUploadUpdateFile || !dwgUploadUpdateFile.name || dwgUploadUpdateFile?.size <= 0 || !dwgUploadUpdateFile.name.endsWith(".dwg")) {
        //    error("dwgファイルが正しく選択されているか確認してください。");
        //    return;
        //}
        //if (!glbUploadUpdateFile || !glbUploadUpdateFile.name || glbUploadUpdateFile?.size <= 0 || !glbUploadUpdateFile.name.endsWith(".glb")) {
        //    error("glbファイルが正しく選択されているか確認してください。");
        //    return;
        //}


        //if (!selectedConstructionModelId || !updatingLocation || !updatingLocation.blockId || !updatingLocation.liftId) {
        //    error("無効なデータ");
        //    return;
        //}
 

        const formData = new FormData();

        // Update the formData object 
        //formData.append(
        //    "dwgFile",
        //    dwgUploadUpdateFile,
        //    dwgUploadUpdateFile.name
        //);

        // Update the formData object 
        //formData.append(
        //    "glbFile",
        //    glbUploadUpdateFile,
        //    glbUploadUpdateFile.name
        //);

        //png
        //if (pngUploadUpdateFiles) {
        //    for (var i = 0; i < pngUploadUpdateFiles?.length; i++) {
        //        formData.append(
        //            "pngFiles",
        //            pngUploadUpdateFiles[i],
        //            pngUploadUpdateFiles[i].name
        //        );
        //    }
        //}

        formData.append(
            "blockId",
            updatingLocation.blockId
        );

        formData.append(
            "liftId",
            updatingLocation.liftId
        );


        formData.append(
            "id",
            updatingLocation.id
        );
        try {
            const response = await dispatch(locationActions.updateLocation(formData));
            if (response.payload && response.payload.success) {
                await dispatch(locationActions.fetchLocationList(selectedConstructionModelId));
                await dispatch(locationActions.resetLocationUpdate());
                await dispatch(locationActions.clearDwgUpdateFileUpload());
                await dispatch(locationActions.clearGlbUpdateFileUpload());
                success("保存が完了しました。");
            } else {
                error(response.payload.errors[0])
            }

        } catch (error: any) {
            error(error)
        }
    }

    const handleDeleteButtonClick = async (id: string) => {
        await dispatch(locationActions.setDeletingLocationId(id));
        setShowDeleteConfirmModal(true);
    }


    const handleChangeInsertValue = (key: string, value: string | number) => {
        dispatch(locationActions.setInsertingtLocationData({ key, value }));
    }

    const handleChangeRowDataValue = (key: string, value: string | number) => {
        dispatch(locationActions.setUpdatingLocationData({ key, value }));
    }

    const handleChangeConstructionModel = (id: string) => {
        dispatch(locationActions.setSelectedConstructionModelId(id));
    }

    const handleDeleteConfirmButtonClick = async () => {
        const response = await dispatch(locationActions.deleteOneLocation(deletingLocationId));
        if (response.payload && response.payload.success) {
            // SKY-160: the deleted 打設場所 may be the one selected for 出荷登録 / 事務所画面
            // (currentLocationId); forget it so those screens do not load a location that no
            // longer exists (出荷登録 turned into a blank page).
            if (localStorage.getItem("currentLocationId") === deletingLocationId) {
                localStorage.removeItem("currentLocationId");
                dispatch(appActions.setCurrentLocationId(""));
            }
            await dispatch(locationActions.fetchLocationList(selectedConstructionModelId));
            setShowDeleteConfirmModal(false);
            success("保存が完了しました。");
        } else {
            setShowDeleteConfirmModal(false);
            if (response.payload.errors.length > 0) {
                error(response.payload.errors[0]);
            }
            else {
                error("削除できません。");
            }
        }

    }

    
    // #8426: png名 = tên file PNG đã cắt hậu tố layer 「○○-○○-○○」 (block-lift-layer); rỗng -> 「なし」
    const displayPngName = (fileName?: string) => {
        if (!fileName) return 'なし';
        const noExt = fileName.replace(/\.[^.]+$/, '');
        const stripped = noExt.replace(/(-\d+){3}$/, '');
        return stripped || noExt;
    };

    const renderList = locationList?.length ? (
        locationList.map((location: ILocationConstructionModel) => (
            <tr key={location.id}>
                <td>
                    {
                        location.id !== updatingLocation.id ?
                            (
                                <>
                                    <input type="button" name="btnRowEdit" value="編" onClick={() => handleUpdateButtonClick(location.id)} />
                                    <input type="button" name="btnRowDelete" value="削" onClick={() => handleDeleteButtonClick(location.id)} className="ml-3" />
                                </>
                            )
                            :
                            (
                                <>
                                    <input type="button" name="btnUpdateSave" value="更新" onClick={() => handleUpdateButtonSaveClick(location.id)} />
                                    <input type="button" name="btnUpdateCancel" value="キャンセル" onClick={() => handleUpdateCancelButtonClick(location.id)} className="ml-3" />
                                </>
                            )

                    }

                </td>

                <td>
                    {
                        location.id !== updatingLocation.id ? location.blockName :
                            (
                                <select
                                    className="form-select-office"
                                    defaultValue={updatingLocation.blockId}
                                    onBlur={(e: { currentTarget: HTMLSelectElement }) => { handleChangeRowDataValue("blockId", e.currentTarget.value) }}
                                >
                                    <option value=""></option>
                                    {blockList.length && blockList.map((p: IBlockModel) => (
                                        <option key={p.id} value={p.id} selected={p.id === updatingLocation?.blockId}>{p.name}</option>
                                    ))}
                                </select>
                            )

                    }

                </td>
                <td>
                    {
                        location.id !== updatingLocation.id ? location.liftName :
                            (
                                <select
                                    className="form-select-office"
                                    defaultValue={updatingLocation.liftId}
                                    onBlur={(e: { currentTarget: HTMLSelectElement }) => { handleChangeRowDataValue("liftId", e.currentTarget.value) }}
                                >
                                    <option value=""></option>
                                    {liftList.length && liftList.map((p: ILiftModel) => (
                                        <option key={p.id} value={p.id} selected={p.id === updatingLocation?.blockId}>{p.name}</option>
                                    ))}
                                </select>
                            )

                    }

                </td>
                {/* SKY-130: 2Dでは打設場所コード列を表示しない（画面設計書_打設場所登録画面_詳細①） */}
                {!is2D && (
                    <td colSpan={location.id !== updatingLocation.id ? 3 : 1}>
                        {location.code}
                    </td>
                )}
                {is2D && location.id !== updatingLocation.id && (
                    <td>{displayPngName(location.pngFileName)}</td>
                )}
                {is2D && location.id === updatingLocation.id && (<td></td>)}
                {
                    !is2D && location.id === updatingLocation.id &&
                    (
                        <td>
                            {/*<input*/}
                            {/*    ref={uploadDwgFileUpdateButtonRef}*/}
                            {/*    type="file"*/}
                            {/*    className="form-control"*/}
                            {/*    name="dwgUploadUpdateFile"*/}
                            {/*    id="dwgUploadUpdateFile"*/}
                            {/*    hidden*/}
                            {/*    onChange={(e: ChangeEvent<HTMLInputElement>) => handleDwgRowFileChange(e)}*/}
                            {/*/>*/}
                            {/*<input type="button" onClick={handleClickUploadDwgUpdateButton} value="dwgファイルを選択" />*/}
                            {/*<p style={{ color: "black" }}>{dwgUploadUpdateFile?.name ?? ""}</p>*/}
                            {/*<input*/}
                            {/*    ref={uploadGlbFileUpdateButtonRef}*/}
                            {/*    type="file"*/}
                            {/*    className="form-control"*/}
                            {/*    name="glbUploadUpdateFile"*/}
                            {/*    id="glbUploadUpdateFile"*/}
                            {/*    hidden*/}
                            {/*    onChange={(e: ChangeEvent<HTMLInputElement>) => handleGlbRowFileChange(e)}*/}
                            {/*/>*/}
                            {/*<input type="button" className="ml-3" onClick={handleClickUploadGlbUpdateButton} value="glbファイルを選択" />*/}
                            {/*<p style={{ color: "black" }}>{glbUploadUpdateFile?.name ?? ""}</p>*/}




                        </td>
                    )

                }

                {
                    !is2D && location.id === updatingLocation.id &&
                    (

                        <td>

                        {/*    <input*/}

                        {/*        type="file"*/}
                        {/*        className="form-control"*/}
                        {/*        name="pngUploadUpdateFiles"*/}
                        {/*        id="pngUploadUpdateFiles"*/}
                        {/*        accept="image/png"*/}
                        {/*        style={{*/}
                        {/*            width: "80%",*/}
                        {/*            height: "30px"*/}
                        {/*        }}*/}
                        {/*        multiple*/}
                        {/*        onChange={(e: ChangeEvent<HTMLInputElement>) => handlePngRowFileChange(e)}*/}
                        {/*    />*/}
                        {/*    <small>pngファイルを選択</small>*/}
                        </td>

                    )

                }




            </tr>
        ))
    ) : (
        <tr>
            <td colSpan={6}>データなし</td>
        </tr>
    );
    

    return (
        <>
            <LoadingSpinner isShowing={locationState.isLoading} />
            <div className="settingItemPageTitle">
                <p>{is2D ? '打重ね画像登録' : '打設場所モデル登録'}</p>
            </div>
            <div className="row">

                <div className="col-6 constructionModelSelectContainer">
                    <label htmlFor="slGeneralModel">{is2D ? '現場名' : '全体モデル'}</label>
                {/*    <select className="form-select ml-3" id="slGeneralModel" defaultValue={selectedConstructionModelId} onChange={e => handleChangeConstructionModel(e.currentTarget.value)}>*/}
                {/*        <option value=""></option>*/}
                {/*        {constructionModelList.length && constructionModelList.map(cm => (*/}
                {/*            <option key={cm.id} value={cm.id}>{cm.name}</option>*/}
                {/*        ))}*/}
                {/*    </select>*/}
                    <select className="form-select ml-3" id="slGeneralModel" value={selectedConstructionModelId} onChange={e => handleChangeConstructionModel(e.currentTarget.value)}>
                        {constructionModelList?.length && constructionModelList?.map(cm => (
                            <option key={cm.id} value={cm.id}>{cm.name}</option>
                        ))}
                    </select>
                </div>


            </div>

            <div className="setting-container">

                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th />
                            <th scope="col">ブロック</th>
                            <th scope="col">リフト</th>
                            {/* SKY-130: 2Dでは打設場所コード列を表示しない（画面設計書_打設場所登録画面_詳細①） */}
                            {!is2D && <th scope="col">打設場所コード</th>}
                            {!is2D && <th scope="col"></th>}
                            <th scope="col">{is2D ? 'pngファイル名' : ''}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderList}

                        <tr className="insertform">
                            <td className="bg-Success" />
                            <td className="bg-Success">
                                ブロック
                            </td>
                            <td className="bg-Success">
                                リフト
                            </td>
                            {/* #8426: hide the モデル図 (dwg/glb) column in 2D mode */}
                            {!is2D && (
                            <td className="bg-Success" colSpan={2}>
                                モデル図
                            </td>
                            )}
                            {/* SKY-130: 2Dでは打設場所コード列を表示しない（画面設計書_打設場所登録画面_詳細①） */}

                            {/* SKY-168: the register row's header is 「pngファイル」 (設計書); the list
                                header above keeps 「pngファイル名」 because it shows the names */}
                            <td className="bg-Success">
                                pngファイル
                            </td>
                        </tr>
                        <tr className="insertform">
                            <td>
                                <input type="button" name="btnAdd" value="登録" onClick={handleAddClick} />
                            </td>
                            <td>

                                <select
                                    className="form-select-office"
                                    defaultValue={insertingLocation.blockId}
                                    onBlur={(e: { currentTarget: HTMLSelectElement }) => { handleChangeInsertValue("blockId", e.currentTarget.value) }}
                                >
                                    <option value=""></option>
                                    {blockList.length && blockList.map((p: IBlockModel) => (
                                        <option key={p.id} value={p.id} selected={p.id === insertingLocation?.blockId}>{p.name}</option>
                                    ))}
                                </select>


                            </td>
                            <td>

                                <select
                                    className="form-select-office"
                                    defaultValue={insertingLocation.liftId}
                                    onBlur={(e: { currentTarget: HTMLSelectElement }) => { handleChangeInsertValue("liftId", e.currentTarget.value) }}
                                >
                                    <option value=""></option>
                                    {liftList.length && liftList.map((p: ILiftModel) => (
                                        <option key={p.id} value={p.id} selected={p.id === insertingLocation?.liftId}>{p.name}</option>
                                    ))}
                                </select>


                            </td>
                            {/* #8426: hide the dwg/glb file pickers in 2D mode */}
                            {!is2D && (
                            <>
                            <td>

                                <input
                                    ref={uploadDwgFileButtonRef}
                                    type="file"
                                    className="form-control"
                                    name="dwgUploadFile"
                                    id="dwgUploadFile"
                                    hidden
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleDwgFileChange(e)}
                                />
                                <input type="button" name="btnDwgFileUpload" onClick={handleClickUploadDwgButton} value="dwgファイル選択" />
                                <p className="fileName">{dwgUploadFile?.name ?? ""}</p>

                            </td>
                            <td>
                                <input
                                    ref={uploadGlbFileButtonRef}
                                    type="file"
                                    className="form-control"
                                    name="glbUploadFile"
                                    id="glbUploadFile"
                                    hidden
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleGlbFileChange(e)}
                                />
                                <input type="button" className="ml-3" name="btnGlbFileUpload" onClick={handleClickUploadGlbButton} value="glbファイル選択" />
                                <p className="fileName">{glbUploadFile?.name ?? ""}</p>
                            </td>
                            </>
                            )}
                            {/* SKY-130: 2Dでは打設場所コード列を表示しないため、スペーサーも不要 */}
                            <td>
                                {/* SKY-168: the browser's own file input (「ファイルの選択」 |
                                    「ファイルが選択されていません」 on one line) per 設計書, instead of
                                    a hidden input driven by a custom button and label */}
                                <input
                                    ref={uploadPngFilesButtonRef}
                                    type="file"
                                    className="form-control"
                                    name="pngUploadFiles"
                                    id="pngUploadFiles"
                                    multiple
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handlePngFilesChange(e)}
                                />

                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>



            <div className="goHomeButtonContainer">
                <input type="button" value="戻る" onClick={() => { history.goBack() }} className="goHomeButton" />
            </div>



            <Modal isActive={showDeleteConfirmModal} onClose={() => handleDeleteCancelButtonClick()} width="30%">
                <div className="modal-body">
                    <p>削除します。よろしいですか？</p>
                </div>
                <div className="modal-footer">
                    <input type="button" onClick={handleDeleteConfirmButtonClick} value="削除" />
                    <input type="button" className="ml-3" onClick={() => { handleDeleteCancelButtonClick() }} value="キャンセル" />
                </div>
            </Modal>

            <ModalBootstrap
                show={isUnderMeshLoading}
                backdrop="static"
                keyboard={false}
                centered
            >
                <ModalBootstrap.Body
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        padding: '40px',
                    }}
                >
                    <Spinner animation="border" role="status" />
                    <div style={{ marginTop: '15px', fontWeight: 'bold' }}>
                        打設場所モデルのデータを登録中です。
                    </div>
                </ModalBootstrap.Body>
            </ModalBootstrap>
        </>
    );
};

export default connect((state: RootState) => ({
    locationState: state.location,
}))(LocationSetting);
